import { useLanguage } from '@/contexts/LanguageContext';

export default function SustainabilityProblem() {
  const { t } = useLanguage();

  const problems = [
    {
      icon: 'ri-fire-line',
      iconColor: 'text-[#C0392B]',
      iconBg: 'bg-[#FDECEA]',
      title: t('廢棄端碳排壓力', 'Carbon Emission Pressure from Waste', '廃棄側の炭素排出圧力'),
      desc: t(
        '焚化與掩埋造成環境負擔，也讓可再利用材料失去價值，形成資源浪費與碳排放的雙重損失。',
        'Incineration and landfilling create environmental burdens and cause reusable materials to lose value, resulting in dual losses of resource waste and carbon emissions.',
        '焼却と埋立は環境負荷をもたらし、再利用可能な材料の価値を失わせ、資源浪費と炭素排出の二重損失を生じさせます。'
      ),
      stat: t('焚化每噸塑膠約排放 2–3 噸 CO₂', 'Incinerating 1 ton of plastic emits approx. 2–3 tons of CO₂', 'プラスチック1トンの焼却で約2〜3トンのCO₂を排出'),
      statColor: 'text-[#C0392B]',
    },
    {
      icon: 'ri-oil-line',
      iconColor: 'text-[#E67E22]',
      iconBg: 'bg-[#FEF3E2]',
      title: t('原生塑膠依賴', 'Virgin Plastic Dependency', 'バージンプラスチック依存'),
      desc: t(
        '原生塑膠製造涉及能源消耗、原料開採與碳排放，持續依賴原生料將加重企業碳足跡負擔。',
        'Virgin plastic manufacturing involves energy consumption, raw material extraction, and carbon emissions. Continued reliance on virgin materials will increase corporate carbon footprint burdens.',
        'バージンプラスチックの製造はエネルギー消費・原料採掘・炭素排出を伴います。バージン材料への継続的な依存は企業のカーボンフットプリント負担を増大させます。'
      ),
      stat: t('原生塑膠製造碳排為再生料的 2–4 倍', 'Virgin plastic manufacturing emits 2–4x more carbon than recycled materials', 'バージンプラスチック製造の炭素排出は再生材料の2〜4倍'),
      statColor: 'text-[#E67E22]',
    },
    {
      icon: 'ri-bar-chart-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('ESG 量化需求', 'ESG Quantification Requirements', 'ESG定量化要件'),
      desc: t(
        '品牌與製造端需要可被追蹤、可被報告、可被驗證的循環材料方案，以回應法規與市場壓力。',
        'Brands and manufacturers need traceable, reportable, and verifiable circular material solutions to respond to regulatory and market pressures.',
        'ブランドと製造業者は、規制と市場の圧力に対応するために、追跡可能・報告可能・検証可能なサーキュラー材料ソリューションを必要としています。'
      ),
      stat: t('全球 ESG 揭露要求持續提升', 'Global ESG disclosure requirements continue to rise', 'グローバルなESG開示要件が継続的に上昇'),
      statColor: 'text-brand-blue',
    },
  ];

  return (
    <section className="section-padding bg-[#F5F8FB]">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
            {t('面臨的挑戰', 'The Challenge', '直面する課題')}
          </p>
          <h2
            className="font-bold text-neutral-dark mb-5"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
          >
            {t(
              '全球塑膠問題，也是企業低碳轉型的關鍵入口',
              'The Global Plastic Problem is Also a Key Entry Point for Corporate Low-Carbon Transformation',
              'グローバルなプラスチック問題は、企業の低炭素転換への重要な入口でもあります'
            )}
          </h2>
          <p className="text-neutral-mid max-w-2xl mx-auto" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
            {t(
              '廢棄塑膠若持續以焚化、掩埋或降階方式處理，將造成資源浪費與碳排放壓力。對企業而言，如何提高再生料比例、降低原生塑膠依賴，已成為 ESG、碳足跡與供應鏈競爭力的重要課題。',
              'If waste plastics continue to be processed through incineration, landfilling, or downgrading, it will cause resource waste and carbon emission pressure. For enterprises, how to increase recycled material ratios and reduce virgin plastic dependency has become an important issue for ESG, carbon footprint, and supply chain competitiveness.',
              '廃プラスチックが焼却・埋立・ダウングレードで処理され続けると、資源浪費と炭素排出圧力が生じます。企業にとって、再生材料比率の向上とバージンプラスチック依存の低減は、ESG・カーボンフットプリント・サプライチェーン競争力の重要課題となっています。'
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {problems.map((p) => (
            <div key={p.title} className="bg-white rounded-card border border-neutral-border p-7" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
              <div className="flex items-center justify-between mb-5">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${p.iconBg}`}>
                  <i className={`${p.icon} ${p.iconColor} text-2xl`}></i>
                </div>
              </div>
              <h3 className="font-semibold text-neutral-dark mb-3" style={{ fontSize: '1.25rem', lineHeight: '1.5' }}>{p.title}</h3>
              <p className="text-neutral-mid mb-5" style={{ fontSize: '1.0625rem', lineHeight: '1.75' }}>{p.desc}</p>
              <div className="flex items-center gap-2 pt-4 border-t border-neutral-border">
                <i className="ri-information-line text-neutral-mid text-sm flex-shrink-0"></i>
                <span className={`text-[0.9375rem] font-medium ${p.statColor}`}>{p.stat}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
